import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createSpecialityType,
  getSpecialityTypeByID,
  specialityTypeImageUpload,
  updateSpecialityType,
} from "./services/specialityType.services";
import { clearSpecialityTypeDetails } from "./specialityTypeSlice";
import { Typography } from "@progress/kendo-react-common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { useLocation, useNavigate } from "react-router-dom";
import FormTextArea from "../../components/formFields/FormTextArea";
import ShadowCard from "../../components/common/ShadowCard";

const CreateSpecialityType: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const gridRef = useRef<any>(null);
  const location = useLocation();
  const SpecialityTypeID = location.state?.SpecialityTypeID;

  const SpecialityTypeDetail = useAppSelector(
    (state) => state.specialityType.SpecialityTypeDetail
  );
  // const SpecialityTypeID = useAppSelector((state) => state.drawer.data);
  const loading = useAppSelector((state) => state.specialityType.loading);

  const [formKey, setFormKey] = useState<number>(1);

  useEffect(() => {
    if (SpecialityTypeID) {
      dispatch(getSpecialityTypeByID(SpecialityTypeID));
    }
  }, [SpecialityTypeID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [SpecialityTypeDetail]);

  useEffect(() => {
    return () => {
      dispatch(clearSpecialityTypeDetails());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const file =
      values?.Image && typeof values?.Image !== "string"
        ? values.Image[0].getRawFile()
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? values.BannerImage[0].getRawFile()
        : "";

    try {
      if (!SpecialityTypeID) {
        const insertPayload = {
          SpecialityType: values.SpecialityType,
          Prefix: values.Prefix,
          MetaTitle: values.MetaTitle,
          MetaKeywords: values.MetaKeywords,
          MetaDescription: values.MetaDescription,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createSpecialityType(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.Image && typeof values?.Image !== "string") ||
              (values?.BannerImage &&
                typeof values?.BannerImage !== "string")) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", file);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("ID", response?.payload?.Data?.ID);

            await dispatch(specialityTypeImageUpload(ImageData));
          }
          navigate("/specialitytype");
          dispatch(clearSpecialityTypeDetails());
        }
      } else {
        const editPayload = {
          ID: SpecialityTypeID,
          SpecialityType: values.SpecialityType,
          Prefix: values.Prefix,
          MetaTitle: values.MetaTitle,
          MetaKeywords: values.MetaKeywords,
          MetaDescription: values.MetaDescription,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateSpecialityType(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.Image && typeof values?.Image !== "string") ||
            (values?.BannerImage && typeof values?.BannerImage !== "string")
          ) {
            ImageData.append("Image", file);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("ID", SpecialityTypeID ? SpecialityTypeID : "");

            await dispatch(specialityTypeImageUpload(ImageData));
          }
          navigate("/specialitytype");
          dispatch(clearSpecialityTypeDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={SpecialityTypeDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{
                  marginRight: 20,
                }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4 className="m-0">
                    {SpecialityTypeID
                      ? "Update Speciality Type"
                      : "Create Speciality Type"}
                  </Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="SpecialityType"
                    label="Speciality Type"
                    placeholder="Speciality Type"
                    component={FormTextField}
                    astrike={true}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Prefix"
                    label="Prefix"
                    placeholder="Prefix"
                    astrike={true}
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MetaTitle"
                    label="Meta Title"
                    placeholder="Meta Title"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MetaKeywords"
                    label="Meta Keywords"
                    placeholder="Meta Keywords"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    wrapperClassName={"w-100"}
                    rows={4}
                    name="MetaDescription"
                    label="Meta Description"
                    placeholder="Meta Description"
                    component={FormTextArea}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    width: "100%",
                  }}
                >
                  <Field
                    name="Image"
                    type="file"
                    accept="image/*"
                    label="Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    width: "100%",
                  }}
                >
                  <Field
                    name="BannerImage"
                    type="file"
                    accept="image/*"
                    label="Banner Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"IsActive"}
                    name={"IsActive"}
                    label={"Status"}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    placeholder="i.e. Active"
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status.value,
                        label: status.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={SpecialityTypeID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/specialitytype")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateSpecialityType;
